import type { ComponentType } from "react";
import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../app/AuthProvider";
import { useTheme } from "../app/ThemeProvider";
import { useBranding, useMe, useNotifications, useMarkNotificationRead, useTenants } from "../api/hooks";
import { sectionsFor, type NavSection } from "../lib/permissions";
import { tenantRoleLabel } from "../lib/format";
import { resolveAppName } from "../lib/branding";
import { Chip, Notice } from "./ui";
import {
  IconDashboard,
  IconDocument,
  IconBuilding,
  IconTruck,
  IconChart,
  IconAudit,
  IconPalette,
  IconKey,
  IconUsers,
  IconOffice,
  IconImage,
  IconSettings,
  IconSun,
  IconMoon,
  IconSystem,
  IconLogout,
  IconBackup,
  IconDatabase,
} from "./icons";

/**
 * Marco del panel: rail de navegación oscuro, barra superior con tenant
 * activo, ficha del usuario y selector de tema, y el área de contenido.
 *
 * El sidebar es oscuro de forma fija (no sigue el tema claro/oscuro del
 * contenido): es el acento visual del panel, no una superficie de lectura de
 * datos, así que no compite por contraste con las tablas y formularios.
 *
 * Cada entrada de navegación declara su `NavSection`; `sectionsFor(role,
 * isSuperAdmin)` (ver `lib/permissions.ts`) decide qué secciones se muestran.
 * Es un filtro de comodidad para no ofrecer opciones que el servidor va a
 * rechazar de todas formas — la regla real vive en `assertRole` en la API.
 *
 * El selector de tenant del super-admin es el único sitio donde se cambia el
 * tenant activo, y lo hace pidiendo un token nuevo (`switch-tenant`): el
 * `tenantId` no viaja nunca en el cuerpo de las operaciones.
 */

interface NavItem {
  to: string;
  label: string;
  section: NavSection;
  icon: ComponentType<{ className?: string }>;
  end?: boolean;
}

const NAV_GROUPS: Array<{ title: string; items: NavItem[] }> = [
  {
    title: "Inicio",
    items: [{ to: "/", label: "Dashboard", section: "dashboard", icon: IconDashboard, end: true }],
  },
  {
    title: "Operación",
    items: [
      { to: "/deca", label: "Documentos DeCA", section: "deca", icon: IconDocument, end: true },
      { to: "/deca/nuevo", label: "Emitir DeCA", section: "deca", icon: IconDocument },
    ],
  },
  {
    title: "Catálogos",
    items: [
      { to: "/contrapartes", label: "Contrapartes", section: "catalogs", icon: IconBuilding },
      { to: "/vehiculos", label: "Vehículos", section: "catalogs", icon: IconTruck },
    ],
  },
  {
    title: "Control",
    items: [
      { to: "/reportes", label: "Reportes", section: "reports", icon: IconChart },
      { to: "/auditoria", label: "Auditoría", section: "audit", icon: IconAudit },
      { to: "/plantillas", label: "Marca y plantillas", section: "templates", icon: IconPalette },
      { to: "/api-keys", label: "Claves de API", section: "apiKeys", icon: IconKey },
      { to: "/financiero", label: "Financiero", section: "financial", icon: IconChart },
    ],
  },
  {
    title: "Administración",
    items: [
      { to: "/usuarios", label: "Usuarios", section: "users", icon: IconUsers },
      { to: "/empresas", label: "Empresas (tenants)", section: "tenants", icon: IconOffice },
      { to: "/marca-app", label: "Marca de la app", section: "branding", icon: IconImage },
      { to: "/planes-facturacion", label: "Planes de facturación", section: "billing", icon: IconKey },
      { to: "/tipos-impuesto", label: "Tipos fiscales", section: "billing", icon: IconKey },
      { to: "/suscripciones", label: "Suscripciones", section: "billing", icon: IconChart },
      { to: "/exportacion-erp", label: "Exportación al ERP", section: "billing", icon: IconBackup },
      { to: "/conversion-erp", label: "Tablas de conversión", section: "billing", icon: IconDatabase },
      { to: "/ajustes-numeracion", label: "Numeración de facturas", section: "billing", icon: IconSettings },
      { to: "/plantilla-factura", label: "Plantilla de factura", section: "billing", icon: IconPalette },
      { to: "/backup", label: "Backup y restauración", section: "backup", icon: IconBackup },
      { to: "/almacenes", label: "Almacenes S3", section: "storage", icon: IconDatabase },
      { to: "/huerfanos-s3", label: "Huérfanos S3", section: "storage", icon: IconDatabase },
    ],
  },
  {
    title: "Cuenta",
    items: [{ to: "/ajustes", label: "Ajustes", section: "settings", icon: IconSettings }],
  },
];

/** Iniciales para la ficha de avatar: primera letra de las dos primeras palabras. */
function initials(fullName: string | null | undefined): string {
  if (!fullName) return "?";
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.[0] ?? "";
  const second = parts.length > 1 ? (parts[1]?.[0] ?? "") : "";
  return (first + second).toUpperCase();
}

const THEME_ICONS = { LIGHT: IconSun, DARK: IconMoon, SYSTEM: IconSystem } as const;
const THEME_SHORT_LABELS = { LIGHT: "Claro", DARK: "Oscuro", SYSTEM: "Sistema" } as const;

// ── Campana de notificaciones ─────────────────────────────────────────────────

const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  DECA_CREATED: "Documento creado",
  DECA_VERSION_UPDATED: "Nueva versión",
  DECA_REPLACED: "Documento sustituido",
  DECA_DOWNLOAD_DISABLED: "Descarga desactivada",
  DECA_SIGNED: "Documento firmado",
  SUBSCRIPTION_RENEWAL_REMINDER: "Recordatorio renovación",
  SUBSCRIPTION_BLOCKED: "Suscripción bloqueada",
  TRIAL_ENDING: "Fin de prueba próximo",
  TRIAL_EXPIRED: "Prueba terminada",
  INVITATION_SENT: "Invitación enviada",
  GENERIC: "Aviso",
};

function NotificationBell() {
  const notifications = useNotifications();
  const markRead = useMarkNotificationRead();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const items = notifications.data ?? [];
  const unread = items.filter((n) => !n.readAt);

  // Cerrar el dropdown al hacer clic fuera
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notificaciones${unread.length > 0 ? `, ${unread.length} sin leer` : ""}`}
        className="relative inline-flex h-9 w-9 flex-none items-center justify-center rounded-md border border-ink-300 bg-white text-ink-600 hover:bg-ink-100 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-300 dark:hover:bg-ink-800"
      >
        {/* Icono campana SVG inline (no requiere nueva importación) */}
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unread.length > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-signal-danger text-[9px] font-bold text-white">
            {unread.length > 9 ? "9+" : unread.length}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-11 z-50 w-80 rounded-xl border border-ink-200 bg-white shadow-xl dark:border-ink-700 dark:bg-ink-900">
          <div className="flex items-center justify-between border-b border-ink-100 px-3 py-2 dark:border-ink-800">
            <p className="text-sm font-semibold text-ink-800 dark:text-ink-100">
              Notificaciones
              {unread.length > 0 && (
                <span className="ml-1.5 rounded-full bg-brand-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                  {unread.length}
                </span>
              )}
            </p>
            <button
              type="button"
              onClick={() => { setOpen(false); navigate("/ajustes"); }}
              className="text-xs text-brand-600 underline hover:text-brand-700 dark:text-brand-400"
            >
              Ver todas
            </button>
          </div>

          <ul className="max-h-72 overflow-y-auto divide-y divide-ink-100 dark:divide-ink-800">
            {items.length === 0 ? (
              <li className="py-6 text-center text-sm text-ink-400">Sin notificaciones</li>
            ) : (
              items.slice(0, 8).map((n) => (
                <li
                  key={n.id}
                  className={`flex items-start gap-2 px-3 py-2.5 ${!n.readAt ? "bg-brand-50/60 dark:bg-brand-900/20" : ""}`}
                >
                  <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${!n.readAt ? "bg-brand-500" : "bg-ink-300 dark:bg-ink-600"}`} aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium text-ink-800 dark:text-ink-100">{n.title}</p>
                    <p className="mt-0.5 truncate text-[11px] text-ink-500 dark:text-ink-400">
                      <span className="mr-1 rounded bg-ink-100 px-1 text-[10px] dark:bg-ink-800">
                        {NOTIFICATION_TYPE_LABELS[n.type] ?? n.type}
                      </span>
                      {new Date(n.createdAt).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                  {!n.readAt && (
                    <button
                      type="button"
                      onClick={() => void markRead.mutate(n.id)}
                      className="shrink-0 text-[10px] text-brand-600 underline hover:text-brand-700 dark:text-brand-400"
                    >
                      ✓
                    </button>
                  )}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function ThemeSwitch() {
  const { preference, cycle } = useTheme();
  const Icon = THEME_ICONS[preference];
  return (
    <button
      type="button"
      onClick={cycle}
      aria-label={`Tema: ${THEME_SHORT_LABELS[preference]}. Pulsa para cambiar`}
      title={`Tema: ${THEME_SHORT_LABELS[preference]}`}
      className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-md border border-ink-300 bg-white text-ink-600 hover:bg-ink-100 dark:border-ink-700 dark:bg-ink-900 dark:text-ink-300 dark:hover:bg-ink-800"
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

export function AppLayout() {
  const auth = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const me = useMe();
  const tenants = useTenants();
  // Estado local para el selector: permite valor optimista mientras la API responde
  const [switchingToTenantId, setSwitchingToTenantId] = useState<string | null>(null);
  const branding = useBranding();

  const appName = resolveAppName(branding.data?.appName);

  // Pestaña del navegador: refleja el nombre de marca configurado, no un
  // "DeKA" fijo en el HTML. Se actualiza en cuanto llega la respuesta de
  // `useBranding()` (o cuando el super-admin lo cambia, tras invalidar la
  // query).
  useEffect(() => {
    document.title = `${appName} · Panel de gestión`;
  }, [appName]);

  const activeTenant = auth.tenants.find((tenant) => tenant.tenantId === auth.activeTenantId);
  const activeSummary = tenants.data?.items.find((tenant) => tenant.id === auth.activeTenantId);

  // Secciones exclusivas de la plataforma (visibles solo sin tenant activo):
  // gestionan la infraestructura global y no tienen sentido dentro de un tenant.
  const PLATFORM_ONLY_SECTIONS: NavSection[] = ["tenants", "branding", "billing", "backup", "storage"];

  // "¿Estamos actualmente en modo tenant?" — anticipamos el destino con
  // switchingToTenantId para que el menú cambie de forma instantánea cuando
  // el usuario elige en el selector, sin esperar a que la API responda.
  // Si se está cambiando a "" (plataforma) o no hay cambio en curso, usamos
  // auth.needsTenant real. Esto evita el flash de menú mezclado durante el
  // tiempo de red del switch.
  const effectiveTenantId = switchingToTenantId !== null
    ? switchingToTenantId          // cambio en curso: usar destino optimista
    : auth.activeTenantId;         // estado real de la sesión
  const effectivelyNeedsTenant = effectiveTenantId === null || effectiveTenantId === "";

  // Un superAdmin operando "como" un tenant debe ver las secciones de ADMIN
  // del tenant, no ALL_SECTIONS. sectionsFor(role, isSuperAdmin=true) ignora
  // el contexto y devuelve todo, por eso pasamos isSuperAdmin=false cuando
  // hay tenant activo. El acceso real en el servidor no cambia.
  const isSuperAdminInPlatformMode = (me.data?.isSuperAdmin ?? false) && effectivelyNeedsTenant;
  const allowedSections = sectionsFor(
    me.data?.role,
    isSuperAdminInPlatformMode,
  );

  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      if (!allowedSections.includes(item.section)) return false;
      if (effectivelyNeedsTenant) {
        // Modo plataforma → solo secciones de plataforma + transversales
        return PLATFORM_ONLY_SECTIONS.includes(item.section) ||
          item.section === "dashboard" ||
          item.section === "settings";
      } else {
        // Modo tenant → excluir secciones exclusivas de plataforma
        return !PLATFORM_ONLY_SECTIONS.includes(item.section);
      }
    }),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[16rem_1fr]">
      {/* En escritorio el menú queda fijo a la altura de la ventana: solo la lista
          de secciones hace scroll y «Salir» siempre es visible, aunque la página sea larga. */}
      <aside className="flex flex-col bg-ink-950 px-4 py-4 lg:sticky lg:top-0 lg:h-screen lg:py-6">
        <div className="mb-6 px-1">
          {branding.data?.wordmarkDark ? (
            // El logotipo horizontal ya lleva el nombre de marca incrustado:
            // no se repite aparte para no duplicarlo. Esta barra es siempre
            // oscura (`bg-ink-950`), de ahí la variante de texto claro.
            <img
              src={branding.data.wordmarkDark}
              alt={appName}
              className="h-9 w-auto max-w-[170px] object-contain object-left"
            />
          ) : (
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-brand-600 font-mono text-sm font-bold text-white">
                D
              </span>
              <div className="min-w-0">
                <p className="truncate font-mono text-base font-bold leading-tight tracking-tight text-white">
                  {appName}
                </p>
                <p className="truncate text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-500">
                  Orden FOM/2861/2012
                </p>
              </div>
            </div>
          )}
        </div>

        <nav aria-label="Secciones del panel" className="flex-1 space-y-5 overflow-y-auto">
          {visibleGroups.map((group) => (
            <div key={group.title}>
              <p className="mb-1.5 px-2.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-ink-500">
                {group.title}
              </p>
              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end ?? false}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors ${
                          isActive
                            ? "bg-brand-600 font-semibold text-white shadow-sm"
                            : "text-ink-300 hover:bg-ink-900 hover:text-white"
                        }`
                      }
                    >
                      <item.icon className="h-[18px] w-[18px] flex-none" />
                      <span className="truncate">{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <button
          type="button"
          onClick={() => {
            auth.logout();
            navigate("/entrar");
          }}
          className="mt-4 flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm text-ink-300 hover:bg-ink-900 hover:text-white"
        >
          <IconLogout className="h-[18px] w-[18px] flex-none" />
          Salir
        </button>
      </aside>

      <div className="flex min-w-0 flex-col">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-ink-200 bg-white px-4 py-3 dark:border-ink-800 dark:bg-ink-900">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            {/* Mostramos el selector si el usuario tiene acceso a varios tenants.
                Para el superAdmin, auth.tenants viene precargado desde AuthProvider
                con el token de plataforma; no dependemos de useTenants() cuyo
                resultado varía según el token activo. */}
            {auth.tenants.length > 0 || me.data?.isSuperAdmin ? (
              <label className="flex items-center gap-2">
                <span className="micro-label">
                  {me.data?.isSuperAdmin ? "Operar como" : "Tenant"}
                </span>
                <select
                  className="field w-auto min-w-[12rem]"
                  value={switchingToTenantId ?? auth.activeTenantId ?? ""}
                  disabled={switchingToTenantId !== null}
                  onChange={(event) => {
                    const newTenantId = event.target.value;
                    // Actualización optimista: el select muestra ya el nuevo valor
                    // mientras la llamada a la API está en curso.
                    setSwitchingToTenantId(newTenantId || null);

                    const doSwitch =
                      newTenantId === ""
                        ? auth.deselectTenant()
                        : auth.activeTenantId === null
                          ? auth.selectTenant(newTenantId)
                          : auth.switchTenant(newTenantId);

                    void doSwitch
                      .then(() => {
                        // Cancelar queries en vuelo: evita que resultados del
                        // tenant anterior lleguen después del cambio.
                        void queryClient.cancelQueries();
                        // Limpiar caché antes de navegar. Con key={activeTenantId}
                        // en AuthenticatedApp (App.tsx), el cambio de tenant
                        // desmonta y vuelve a montar todo el árbol de páginas y
                        // hooks, garantizando que arrancan con el token y
                        // activeTenantId correctos sin race conditions. Esta
                        // limpieza evita que el caché del QueryClient (que vive
                        // por encima de AuthenticatedApp) sirva datos del tenant
                        // anterior al nuevo árbol antes de que los hooks hagan
                        // sus propios fetches.
                        queryClient.removeQueries();
                        navigate("/", { replace: true, state: { tenantSwitch: newTenantId || "platform" } });
                      })
                      .catch(() => {
                        // Si falla, revertir selección optimista
                        setSwitchingToTenantId(null);
                      })
                      .finally(() => {
                        setSwitchingToTenantId(null);
                      });
                  }}
                >
                  {/* Opción "Plataforma" solo para superAdmin */}
                  {me.data?.isSuperAdmin && (
                    <option value="">— Plataforma —</option>
                  )}
                  {/* auth.tenants es la fuente de verdad: para superAdmin se carga
                      en AuthProvider con el token de plataforma y no cambia al
                      seleccionar un tenant; para usuarios normales son sus tenants
                      de login. No usamos useTenants() aquí porque varía con el token. */}
                  {auth.tenants.map((tenant) => (
                    <option key={tenant.tenantId} value={tenant.tenantId}>
                      {tenant.tenantName}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <div>
                <p className="micro-label">Tenant</p>
                <p className="text-sm font-medium text-ink-800 dark:text-ink-100">
                  {activeTenant?.tenantName ?? "—"}
                </p>
              </div>
            )}
            {activeSummary?.manuallyBlocked ? (
              <Chip tone="danger">Tenant bloqueado</Chip>
            ) : null}
          </div>

          <div className="flex items-center gap-3">
            <NotificationBell />
            <ThemeSwitch />
            <div className="flex items-center gap-2.5 border-l border-ink-200 pl-3 dark:border-ink-800">
              <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-brand-50 text-xs font-semibold text-brand-700 dark:bg-brand-950/60 dark:text-brand-200">
                {initials(me.data?.fullName)}
              </span>
              <div className="hidden text-right sm:block">
                <p className="text-sm font-medium leading-tight text-ink-800 dark:text-ink-100">
                  {me.data?.fullName ?? "—"}
                </p>
                <p className="text-xs leading-tight text-ink-500 dark:text-ink-400">
                  {me.data?.isSuperAdmin ? "Super-admin" : tenantRoleLabel(me.data?.role)}
                  {activeTenant ? ` · ${activeTenant.tenantName}` : ""}
                </p>
              </div>
            </div>
          </div>
        </header>

        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6">
          {activeSummary?.manuallyBlocked ? (
            <div className="mb-4">
              <Notice tone="warn">
                Este tenant está bloqueado manualmente: no se pueden emitir ni modificar documentos.
                La descarga de los DeCA ya emitidos sigue disponible, como exige la norma.
              </Notice>
            </div>
          ) : null}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
